import { formatTime } from '../formatters';
import type { ItineraryBoardingStep } from '../itinerarySteps';
import { JourneyPlanOperatorDetails } from './OperatorDetails';
import { JourneyPlanStepCard } from './StepCard';
import styles from './JourneyPlanBoardingStepDetails.module.css';

interface JourneyPlanBoardingStepDetailsProps {
  step: ItineraryBoardingStep;
  onShowStopDeparturesDetails?: (step: ItineraryBoardingStep) => void;
  onShowLineTimetableDetails?: (step: ItineraryBoardingStep) => void;
}

function getExpectedStatusLine(step: ItineraryBoardingStep): string {
  if (!step.expectedDepartureDateTime) {
    return '';
  }

  const expectedTime = formatTime(step.expectedDepartureDateTime);

  switch (step.expectedState) {
    case 'delayed':
      return `Delayed. Expected: ${expectedTime}`;
    case 'early':
      return `Early. Expected: ${expectedTime}`;
    case 'as-scheduled':
    default:
      return `Expected: ${expectedTime}`;
  }
}

function getExpectedStatusTone(
  step: ItineraryBoardingStep,
): 'default' | 'success' | 'warning' {
  if (step.expectedState === 'as-scheduled') {
    return 'success';
  }

  if (step.expectedState === 'delayed' || step.expectedState === 'early') {
    return 'warning';
  }

  return 'default';
}

export function JourneyPlanBoardingStepDetails({
  step,
  onShowStopDeparturesDetails,
  onShowLineTimetableDetails,
}: JourneyPlanBoardingStepDetailsProps) {
  return (
    <div className={styles.stack}>
      {step.showDeparturesLink ? (
        <JourneyPlanStepCard
          title="View Departures"
          icon="rti"
          tone={getExpectedStatusTone(step)}
          meta={getExpectedStatusLine(step)}
          description={
            step.expectedStateAffectsTransfers
              ? 'This may affect transfers'
              : undefined
          }
          onClick={() => onShowStopDeparturesDetails?.(step)}
        />
      ) : null}
      {step.showTimetableLink ? (
        <JourneyPlanStepCard
          title="View Timetable"
          icon="timetable"
          onClick={() => onShowLineTimetableDetails?.(step)}
        />
      ) : null}
      {step.operator ? (
        <JourneyPlanOperatorDetails mode={step.mode} operator={step.operator} />
      ) : null}
      {step.notes.map((note, noteIndex) => (
        <p key={`${step.key}-note-${noteIndex}`} className={styles.note}>
          {note}
        </p>
      ))}
    </div>
  );
}
